/**
 * Per-engine drafts of the connection dialog: pure rules (Option C, C0, plan §3.1 "Draft transaction contract").
 *
 * Shared fields (Name, Color, AI permission) always live in the form and are never part of a draft. Every other field,
 * the password and the SSH block included, belongs to the engine it was typed for: switching engines captures the
 * current engine's values and field meta, and returning restores them unchanged. A first visit seeds the engine's
 * defaults; only the server, login and SSH block carry over, only between network engines, and never a password or a
 * typed SSH secret.
 *
 * Nothing here touches a store, storage or IPC: drafts live in a ref for the dialog's lifetime (`useEngineDrafts`), so
 * no credential outlives the dialog. `engineDrafts.boundary.test.ts` pins that this module imports none of them.
 */
import { azureStorageEngineDefaults } from '../../components/azureStoragePreset';
import { connectionDefaultValues, type ConnectionFormValues, type DatabaseEngine } from '../../components/connectionFormSchema';

export const SHARED_FIELDS = ['alias', 'color', 'mcpPermissionLevel'] as const;
export type SharedField = (typeof SHARED_FIELDS)[number];
export type SharedValues = Pick<ConnectionFormValues, SharedField>;
export type EngineScopedValues = Omit<ConnectionFormValues, SharedField>;

/** The part of TanStack's field meta a draft keeps: validation errors are recomputed, never restored. */
export type FieldMetaFlags = { isDirty: boolean; isTouched: boolean; isBlurred: boolean };
export type FieldMetaMap = Record<string, FieldMetaFlags>;

/** A Load databases answer, bound to the target it listed (`targetFingerprint` at load time). */
export type LoadedDatabases = { databases: string[]; target: string };

export type EngineDraft = { values: EngineScopedValues; meta: FieldMetaMap; loaded?: LoadedDatabases };

/** Engines that dial a host and port (and may tunnel through SSH): the only ones between which anything carries. */
export const NETWORK_ENGINES: readonly DatabaseEngine[] = ['mssql', 'pgsql', 'mysql', 'oracle'];

export function isNetworkEngine(engine: DatabaseEngine | undefined): boolean {
  return engine !== undefined && NETWORK_ENGINES.includes(engine);
}

export function isSharedField(field: string): boolean {
  return (SHARED_FIELDS as readonly string[]).includes(field);
}

/** What a first visit to `engine` starts from (what the engine tile used to write, applied to a clean form). */
export function engineDefaultValues(engine: DatabaseEngine): ConnectionFormValues {
  const base: ConnectionFormValues = {
    ...connectionDefaultValues,
    sshTunnel: { enabled: false, host: '', port: 22, user: '', auth: { kind: 'agent' } },
  };
  switch (engine) {
    case 'mssql':
      return base;
    case 'pgsql':
    case 'oracle':
      return { ...base, engine, authentication: 'Password', port: '<auto>' };
    case 'mysql':
      // Secure by default: trust off, so turning SSL on verifies the certificate.
      return { ...base, engine, authentication: 'Password', port: '<auto>', trustServerCertificate: false };
    case 'sqlite':
      return { ...base, engine, authentication: 'No password', port: '0' };
    case 'kusto':
      return { ...base, engine, authentication: 'Microsoft Entra ID (Interactive Browser)', port: '0' };
    case 'azurestorage':
      return { ...base, engine, ...azureStorageEngineDefaults() } as ConnectionFormValues;
  }
}

export function sharedValuesOf(values: ConnectionFormValues): SharedValues {
  return { alias: values.alias, color: values.color, mcpPermissionLevel: values.mcpPermissionLevel };
}

export function scopedValuesOf(values: ConnectionFormValues): EngineScopedValues {
  const scoped: Record<string, unknown> = { ...values };
  for (const field of SHARED_FIELDS) delete scoped[field];
  return scoped as EngineScopedValues;
}

export function mergeShared(scoped: EngineScopedValues, shared: SharedValues): ConnectionFormValues {
  return { ...scoped, ...shared } as ConnectionFormValues;
}

type MetaLike = Record<string, { isDirty?: boolean; isTouched?: boolean; isBlurred?: boolean } | undefined>;

function flagsOf(meta: MetaLike, keep: (field: string) => boolean): FieldMetaMap {
  const flags: FieldMetaMap = {};
  for (const [field, entry] of Object.entries(meta)) {
    if (!entry || !keep(field)) continue;
    flags[field] = { isDirty: entry.isDirty === true, isTouched: entry.isTouched === true, isBlurred: entry.isBlurred === true };
  }
  return flags;
}

/** The engine-scoped fields' dirty / touched / blurred flags (a draft's `meta`). */
export function captureScopedMeta(meta: MetaLike): FieldMetaMap {
  return flagsOf(meta, (field) => !isSharedField(field));
}

/** The shared fields' flags: they survive every engine switch, like their values. */
export function captureSharedMeta(meta: MetaLike): FieldMetaMap {
  return flagsOf(meta, isSharedField);
}

/** Only the host, bastion and login shape — never the typed bastion password or key passphrase. */
function portableSshTunnel(ssh: ConnectionFormValues['sshTunnel']): ConnectionFormValues['sshTunnel'] {
  if (!ssh) return ssh;
  const { password: _password, passphrase: _passphrase, ...rest } = ssh as typeof ssh & { password?: string; passphrase?: string };
  return rest;
}

/**
 * The values of an engine visited for the first time in this dialog: its defaults, plus the server, login and SSH
 * block of the engine the user comes from when both are network engines. The password never carries.
 */
export function seedEngineValues(target: DatabaseEngine, from?: ConnectionFormValues): EngineScopedValues {
  const seeded = engineDefaultValues(target);
  if (from && isNetworkEngine(from.engine) && isNetworkEngine(target)) {
    seeded.server = from.server;
    seeded.login = from.login;
    seeded.sshTunnel = portableSshTunnel(from.sshTunnel) ?? seeded.sshTunnel;
  }
  return scopedValuesOf(seeded);
}

/** Canonical JSON: keys sorted, `undefined` dropped — two value sets compare equal whatever their key order. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, entry: unknown) =>
    entry && typeof entry === 'object' && !Array.isArray(entry)
      ? Object.fromEntries(Object.entries(entry as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
      : entry,
  );
}

export function sameValues(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b);
}

/**
 * A user edit of Server (typed, Tab-filled, or patched by a paste or an Azure pick) makes the server the user's: an
 * Oracle TNS connection no longer re-synthesizes it from its target on Save, even when the value equals the synthesized
 * one (C1 review C1-02). Not an edit of its own: no dirty or touched flags.
 */
export function revokeTnsServerProvenance(form: { state: { values: Partial<ConnectionFormValues> }; setFieldValue: (field: 'oracleServerFromTnsTarget', value: boolean, options: { dontUpdateMeta: boolean }) => void }): void {
  if (form.state.values.oracleServerFromTnsTarget) form.setFieldValue('oracleServerFromTnsTarget', false, { dontUpdateMeta: true });
}

/**
 * What a Load databases list answers for: the engine, the address, the sign-in and the tunnel. A list is shown only
 * while the live form still has this fingerprint (editing Server hides it; undoing the edit shows it again).
 */
export function targetFingerprint(values: Partial<ConnectionFormValues>): string {
  const ssh = values.sshTunnel?.enabled
    ? { host: values.sshTunnel.host, port: values.sshTunnel.port, user: values.sshTunnel.user }
    : null;
  return JSON.stringify([
    values.engine ?? null,
    values.server ?? '',
    String(values.port ?? ''),
    values.authentication ?? null,
    values.login ?? '',
    ssh,
    values.oracleConnectionStringType ?? 'ezconnect',
    values.oracleServiceName ?? '',
    values.oracleUseSID === true,
    values.oracleTnsDescriptor ?? '',
    values.oracleTnsAlias ?? '',
    values.oracleTnsAdmin ?? '',
  ]);
}
